// src/telegram.ts
declare global {
  interface Window {
    Telegram?: { WebApp?: any };
  }
}

/** Direct alias in case you want to call WebApp methods inline */
export const TWA =
  typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;

export function getWA() {
  return typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;
}

/** Apply Telegram theme + bar colors. */
export function applyThemeAndBars(wa: any = getWA()) {
  try {
    document.documentElement.className = wa?.colorScheme || "";
    const tp = wa?.themeParams || {};
    document.body.style.setProperty("--tg-theme-bg-color", tp.bg_color || "");
    wa?.setHeaderColor?.("secondary_bg_color");
    wa?.setBottomBarColor?.(tp.bottom_bar_bg_color || tp.bg_color || "#111111");
    wa?.setBackgroundColor?.(tp.bg_color || "#111111");
  } catch {}
}

/** Minimal init: ready/expand + theme updates. Safe areas handled in CSS. */
export function initTelegram() {
  const wa = getWA();
  if (!wa) return;

  wa.ready?.();
  wa.expand?.();

  applyThemeAndBars(wa);
  wa.onEvent?.("themeChanged", () => applyThemeAndBars(wa));

  // QoL
  wa.enableVerticalSwipes?.();
  wa.disableClosingConfirmation?.();
}

/** Optional: request fullscreen explicitly (kept for convenience). */
export function requestFullscreenNow() {
  const wa = getWA();
  try {
    if (wa?.isVersionAtLeast?.("8.0") && wa?.requestFullscreen) {
      wa.requestFullscreen();
    } else {
      wa?.expand?.();
    }
  } catch {}
}

export function exitFullscreenNow() {
  const wa = getWA();
  try {
    wa?.exitFullscreen?.();
  } catch {}
}

/** Helpers you asked to keep */
export function showAlert(message: string) {
  const wa = getWA();
  if (wa?.showAlert) wa.showAlert(message);
  else alert(message);
}

/** Native confirmation popup. Fallback to window.confirm on web. */
export function confirmPopup(
  title: string,
  message: string,
  confirmText = "OK",
  cancelText = "Cancel"
): Promise<boolean> {
  const wa = getWA();
  if (wa?.showPopup) {
    return new Promise((resolve) => {
      wa.showPopup(
        {
          title,
          message,
          buttons: [
            { id: "ok", type: "default", text: confirmText },
            { type: "cancel", text: cancelText },
          ],
        },
        (btnId: string) => resolve(btnId === "ok")
      );
    });
  }
  return Promise.resolve(window.confirm(message));
}

export function openLink(url: string, tryInstantView = false) {
  const wa = getWA();
  if (wa?.openLink) wa.openLink(url, { try_instant_view: tryInstantView });
  else window.open(url, "_blank", "noopener");
}

export function openTelegramLink(url: string) {
  const wa = getWA();
  if (wa?.openTelegramLink) wa.openTelegramLink(url);
  else window.open(url, "_blank", "noopener");
}

export function guardClose(enable = true) {
  try {
    const wa = window.Telegram?.WebApp;
    if (enable) wa?.enableClosingConfirmation?.();
    else wa?.disableClosingConfirmation?.();
  } catch { /* noop */ }
}

/** Optional: handle Telegram's BackButton (top-left), not the Close button */
export function setupBackButton(onClick: () => void) {
  const wa = window.Telegram?.WebApp;
  try {
    wa?.BackButton?.show?.();
    wa?.onEvent?.("backButtonClicked", onClick);
    return () => {
      wa?.offEvent?.("backButtonClicked", onClick);
      wa?.BackButton?.hide?.();
    };
  } catch {
    return () => {};
  }
}
